import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaArrowLeft,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaMapMarkerAlt,
  FaDownload,
  FaSync,
} from "react-icons/fa";
import VolunteerInvites from "./VolunteerInvites";

const fmt = (rfc) => {
  if (!rfc) return "—";
  const d = new Date(rfc);
  if (Number.isNaN(d.getTime())) return rfc;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const dayKey = (n) => `day-${n}`;

const EventAttendance = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const apiDomain = process.env.REACT_APP_API_DOMAIN;
  const eventName = location.state?.eventName || `Event #${eventId}`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roster, setRoster] = useState({ tickets: [], days: [], venue: null, total: 0 });
  const [activeDay, setActiveDay] = useState(1);
  const [search, setSearch] = useState("");

  const fetchRoster = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${apiDomain}/api/v2/admin/events/roster`, {
        params: { event_id: eventId, t: Date.now() },
      });
      const data = res.data || {};
      setRoster({
        tickets: data.tickets || [],
        days: data.days || [],
        venue: data.venue || null,
        total: data.total || 0,
      });
      if ((data.days || []).length > 0) {
        setActiveDay((d) => (d > data.days.length ? 1 : d));
      }
    } catch (err) {
      console.error("roster fetch failed:", err);
      setError("Failed to load attendance roster.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster.tickets;
    return roster.tickets.filter((row) => {
      const t = row.ticket || {};
      return (
        (t.ticket_id || "").toLowerCase().includes(q) ||
        (t.user_id || "").toLowerCase().includes(q) ||
        (t.email_address || "").toLowerCase().includes(q)
      );
    });
  }, [roster, search]);

  const presentCount = useMemo(() => {
    const k = dayKey(activeDay);
    return roster.tickets.filter((row) => row.attendance && row.attendance[k]).length;
  }, [roster, activeDay]);

  const absentCount = roster.total - presentCount;

  const exportCsv = () => {
    const k = dayKey(activeDay);
    const rows = [
      ["ticket_id", "user_id", "role", "email", "issued_at", "day", "status", "scanned_at", "scanned_by"],
    ];
    for (const row of roster.tickets) {
      const t = row.ticket || {};
      const a = row.attendance?.[k];
      rows.push([
        t.ticket_id || "",
        t.user_id || "",
        t.role || "",
        t.email_address || "",
        t.issued_at || "",
        String(activeDay),
        a ? "present" : "absent",
        a?.scanned_at || "",
        a?.scanned_by || "",
      ]);
    }
    const csv = rows
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${eventId}_day-${activeDay}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const dayList = roster.days?.length > 0 ? roster.days : [{ day_number: 1 }];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-gray-200"
            >
              <FaArrowLeft />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Attendance</h1>
              <p className="text-sm text-gray-600">{eventName} · #{eventId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchRoster}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white border rounded-lg hover:bg-gray-100"
            >
              <FaSync /> Refresh
            </button>
            <button
              type="button"
              onClick={exportCsv}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              <FaDownload /> Export Day {activeDay} CSV
            </button>
          </div>
        </div>

        {roster.venue && (
          <div className="mb-4 p-4 bg-white rounded-lg border flex items-start gap-3">
            <FaMapMarkerAlt className="text-amber-600 mt-1 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-gray-800">{roster.venue.name || "(unnamed venue)"}</div>
              <div className="text-sm text-gray-600">
                {[roster.venue.address, roster.venue.city, roster.venue.state, roster.venue.pincode]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </div>
              {roster.venue.map_url && (
                <a
                  href={roster.venue.map_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Open in Maps →
                </a>
              )}
            </div>
          </div>
        )}

        {/* Volunteer invite codes for this event */}
        <VolunteerInvites eventId={eventId} />

        {/* Day tabs */}
        {dayList.length > 1 && (
          <div className="flex border-b mb-4">
            {dayList.map((d) => (
              <button
                key={d.day_number}
                type="button"
                onClick={() => setActiveDay(d.day_number)}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeDay === d.day_number
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Day {d.day_number}
                {d.start && (
                  <span className="ml-2 text-xs text-gray-400">({fmt(d.start)})</span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Summary chips */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="p-4 bg-white rounded-lg border">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total Tickets</div>
            <div className="text-2xl font-bold text-gray-800">{roster.total}</div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="text-xs text-green-700 uppercase tracking-wide">Present (Day {activeDay})</div>
            <div className="text-2xl font-bold text-green-700">{presentCount}</div>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="text-xs text-red-700 uppercase tracking-wide">Absent (Day {activeDay})</div>
            <div className="text-2xl font-bold text-red-700">{absentCount}</div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4 relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by ticket id, user id, or email…"
            className="w-full pl-10 pr-4 py-2 border rounded-lg bg-white"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading roster…</div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {roster.total === 0
                ? "No tickets issued for this event yet."
                : "No tickets match your search."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Ticket</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">User</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Role</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Issued</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Day {activeDay}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Scanned</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Scanned By</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map((row) => {
                    const t = row.ticket || {};
                    const a = row.attendance?.[dayKey(activeDay)];
                    return (
                      <tr key={t.ticket_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs">{t.ticket_id}</td>
                        <td className="px-4 py-3 text-xs break-all">{t.user_id}</td>
                        <td className="px-4 py-3 capitalize">{t.role}</td>
                        <td className="px-4 py-3 text-gray-600">{fmt(t.issued_at)}</td>
                        <td className="px-4 py-3">
                          {a ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                              <FaCheckCircle /> Present
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                              <FaTimesCircle /> Absent
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{fmt(a?.scanned_at)}</td>
                        <td className="px-4 py-3 text-gray-600">{a?.scanned_by || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventAttendance;
